import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import type { Locale } from 'date-fns';
import { areIntervalsOverlapping, format, getDay, max as dateMax, min as dateMin } from 'date-fns';
import { toDate, utcToZonedTime } from 'date-fns-tz';
import type { ScaleTime } from 'd3-scale';
import { isEmpty, isNull, range } from 'lodash-es';
import type { Day } from './hooks/useCalendarScales';
import type { RenderBlock } from './components/CalendarRow';
import { CALENDAR_SETTINGS, MINUTES_IN_WEEK } from './constants';
import { applyModifierForTimezone } from './weekPicker/WeekPicker';
import { LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT } from '../../constants';

export const calendarTimezone = 'Etc/UTC';

interface TimeSpan {
    from: Date;
    to: Date;
}

export interface TimeWindow {
    name: string;
    warningLevel: number | null;
    alarmLevel: number | null;
    correction?: number;
    startDay: number;
    endDay: number;
    startTime: number;
    endTime: number;
    sourceTimeWindow?: TimeWindow;
}

export type TimeWindowOnly = Pick<TimeWindow, 'startDay' | 'endDay' | 'startTime' | 'endTime'>;

interface BlockPeriod {
    timeSpan: TimeSpan;
    timeWindow?: TimeWindow;
}

export interface BlockToRender {
    Element: RenderBlock['Element'];
    data: BlockPeriod[];
    elementProps?: Record<string, unknown> & { currentBlock?: TimeWindow };
}

// Function that converts a date to a calendar date in the UTC timezone.
// This ensures that dates are standardized by removing any timezone offset.
// For example:
// 2024-03-31 00:00:00+01:00 becomes 2024-03-31 00:00:00+00:00
// 2024-03-31 23:59:59+02:00 becomes 2024-03-31 23:59:59+00:00
// This helps in eliminating issues with variable-length days (e.g., 23 or 25 hours)
// caused by daylight saving time changes, ensuring consistent formatting.
export function toCalendarDate(date: Date): Date {
    return toDate(format(date, LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT), {
        timeZone: calendarTimezone,
    });
}

export function createTimeSpanObject(from: Date, to: Date): { timeSpan: TimeSpan } {
    return { timeSpan: { from, to } };
}

export function getProgressBarColorClass(
    measuredValue?: number | null,
    warningLevel?: number | null,
    alarmLevel?: number | null
): string | null {
    if (!measuredValue || isNull(measuredValue)) return null;

    if (alarmLevel && measuredValue >= alarmLevel) return 'bg-error';
    if (warningLevel && measuredValue >= warningLevel) return 'bg-warning';
    return 'bg-success';
}

export function getLdenIcon(
    measuredValue?: number | null,
    warningLevel?: number | null,
    alarmLevel?: number | null
): { icon: IconDefinition; iconColor: string } {
    if (alarmLevel && measuredValue && measuredValue > alarmLevel)
        return { icon: faExclamationTriangle, iconColor: 'text-error' };
    if (warningLevel && measuredValue && measuredValue > warningLevel)
        return { icon: faExclamationTriangle, iconColor: 'text-warning' };
    return { icon: faCircleCheck, iconColor: 'text-success' };
}

export function getEventInfo(
    events: Array<{ __typename?: string; timestamp?: string; uploading?: unknown }> | undefined,
    eventName: string
): { date: Date | null; uploads: unknown } {
    const event = events?.find(({ __typename }) => __typename === eventName);
    return {
        date: event?.timestamp ? new Date(event.timestamp) : null,
        uploads: event?.uploading ?? null,
    };
}

export function getDayPeriod(locale: Locale, period: string): string {
    return locale.localize!.dayPeriod(period) as string;
}

export function isTimeWindowEqual(a: TimeWindowOnly, b: TimeWindowOnly): boolean {
    return (
        a.startDay === b.startDay &&
        a.startTime === b.startTime &&
        a.endDay === b.endDay &&
        a.endTime === b.endTime
    );
}

// Converts 1000, 1330, etc to minutes since midnight
function militaryTimeToMinutes(militaryTime: number): number {
    const hours = Math.floor(militaryTime / 100);
    const minutes = militaryTime % 100;
    return hours * CALENDAR_SETTINGS.MINUTES_IN_HOUR + minutes;
}

export function minutesSinceTemplateStart(day: number, militaryTime: number): number {
    return (
        day * CALENDAR_SETTINGS.HOURS_IN_DAY * CALENDAR_SETTINGS.MINUTES_IN_HOUR +
        militaryTimeToMinutes(militaryTime)
    );
}

/**
 * A block can wrap across the template week boundary (e.g. Saturday evening
 * into Sunday), in which case `endDay` is anchored earlier in the week than
 * `startDay`. This returns the pair of minutes-since-template-start with the
 * end normalized into the following week so that `end - start` is always the
 * real, non-negative duration of the block.
 */
export function getWeekWrappedMinutes(window: TimeWindowOnly): [start: number, end: number] {
    const start = minutesSinceTemplateStart(window.startDay, window.startTime);
    const end = minutesSinceTemplateStart(window.endDay, window.endTime);
    return end < start ? [start, end + MINUTES_IN_WEEK] : [start, end];
}

export function areBlocksOverlapping(blockA: TimeWindowOnly, blockB: TimeWindowOnly): boolean {
    const aStart = minutesSinceTemplateStart(blockA.startDay, blockA.startTime);
    const aEnd = minutesSinceTemplateStart(blockA.endDay, blockA.endTime);
    const bStart = minutesSinceTemplateStart(blockB.startDay, blockB.startTime);
    const bEnd = minutesSinceTemplateStart(blockB.endDay, blockB.endTime);

    return aStart < bEnd && bStart < aEnd;
}

export function checkCollision(blockToCheck: TimeWindow, customTemplate: TimeWindow[]): boolean {
    return customTemplate.some((block) => areBlocksOverlapping(block, blockToCheck));
}

export function generateBlocksPerDay({
    scalesPerDay,
    timezone,
    blocksToRender,
}: {
    scalesPerDay: Map<Day, ScaleTime<number, number>>;
    timezone: string;
    blocksToRender: BlockToRender[];
}): Map<Day, RenderBlock[]> {
    const days = Array.from(scalesPerDay.keys());
    const perDay = new Map<Day, RenderBlock[]>(days.map((day) => [day, []]));

    blocksToRender.forEach(({ Element, data, elementProps }) => {
        data.forEach((period) => {
            const startInCalendar = toCalendarDate(utcToZonedTime(period.timeSpan.from, timezone));
            const endInCalendar = toCalendarDate(utcToZonedTime(period.timeSpan.to, timezone));

            days.forEach((day) => {
                const scale = scalesPerDay.get(day);
                const blocks = perDay.get(day);

                if (!scale || !blocks) {
                    throw new Error('Scale or blocks not found for the given day');
                }

                if (areIntervalsOverlapping({ start: startInCalendar, end: endInCalendar }, day)) {
                    const inScaleStart = dateMax([startInCalendar, day.start]);
                    const inScaleEnd = dateMin([endInCalendar, day.end]);

                    const left = scale(inScaleStart);
                    const width = scale(inScaleEnd) - left;

                    const isActive =
                        elementProps?.currentBlock &&
                        period.timeWindow?.sourceTimeWindow &&
                        isTimeWindowEqual(
                            period.timeWindow.sourceTimeWindow,
                            elementProps.currentBlock
                        );

                    blocks.push({
                        Element,
                        elementProps: {
                            ...elementProps,
                            isActive,
                        },
                        props: { period, left, width },
                    });
                }
            });
        });
    });

    return perDay;
}

export function dateToMilitary(date: Date): { day: number; time: number } {
    return { day: getDay(date), time: parseInt(format(date, 'Hmm'), 10) };
}

function getParsedTime(time: number): { hours: number; minutes: number } {
    const minutes = time % 100;
    const hours = (time - minutes) / 100;

    return { hours, minutes };
}

export function militaryToText(time: number): string {
    const { hours, minutes } = getParsedTime(time);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// First Sunday since epoch. We use Sunday as that is day 0 in the protobuf.
export const templateStartInUtc = new Date('1970-01-04T00:00:00.000Z');

export function extendTemplate(template: TimeWindow[] | null, repeat: number): TimeWindow[] | null {
    if (isEmpty(template)) return null;
    return range(repeat).flatMap((week) =>
        (template as TimeWindow[]).map((timeWindow) => {
            const offset = week * CALENDAR_SETTINGS.DAYS_IN_WEEK;
            const newStartDay = timeWindow.startDay + offset;
            let newEndDay = timeWindow.endDay + offset;

            if (timeWindow.endDay < timeWindow.startDay) {
                newEndDay += CALENDAR_SETTINGS.DAYS_IN_WEEK;
            }

            return {
                ...timeWindow,
                startDay: newStartDay,
                endDay: newEndDay,
                sourceTimeWindow: timeWindow,
            } as TimeWindow;
        })
    );
}

/**
 * Converts a calendar-relative day number and "military" time (e.g., 1530 for 15:30)
 * into an absolute UTC Date object.
 *
 * Details:
 * 1. We start from a fixed calendar UTC anchor date (`templateStartInUtc`, e.g.,
 *    1970-01-04T00:00:00Z). However, directly modifying a JavaScript `Date` object
 *    affects it in the browser's local timezone, which can cause subtle bugs if the
 *    developer or user is not on UTC time.
 *
 *    Example:
 *    - `new Date('1970-01-04T00:00:00.000Z')` represents midnight UTC.
 *    - In a browser set to GMT-0800, this date appears as `1970-01-03T16:00:00.000` (local time).
 *    - If you naïvely use `setDate` or `setHours`, you'll end up adjusting the local
 *      date, not the UTC one.
 *
 * 2. To ensure consistent UTC results:
 *    - The anchor date is first converted to the target calendar timezone (UTC).
 *    - The day offset and "military" time are applied in this timezone context.
 *    - Finally, this corrected date is converted *back* to a UTC Date object for
 *      storage or comparison.
 *
 *    This approach always results in the intended date and time in UTC, regardless of the runtime's
 *    local timezone.
 *
 *    The helper `applyModifierForTimezone` encapsulates this logic.
 *
 * @param dayNumber - The offset in days from the template UTC start date.
 * @param time      - The time, in "military" format as a number (e.g., 1530).
 * @returns         - A UTC Date object representing the desired calendar date and time.
 */
export function getFullDateFromNumber(dayNumber: number, time: number): Date {
    return applyModifierForTimezone(templateStartInUtc, calendarTimezone, (zonedDate) => {
        zonedDate.setDate(zonedDate.getDate() + dayNumber);
        const { hours, minutes } = getParsedTime(time);
        zonedDate.setHours(hours, minutes, 0, 0);
        return zonedDate;
    });
}

export function formatTemplateData<T>(template: TimeWindow[] | null): Array<{
    timeWindow: TimeWindow;
    value: T | null;
    timeSpan: TimeSpan;
}> {
    if (!template) return [];
    return template.map((timeWindow) => ({
        timeWindow,
        value: null,
        ...createTimeSpanObject(
            getFullDateFromNumber(timeWindow.startDay, timeWindow.startTime),
            getFullDateFromNumber(timeWindow.endDay, timeWindow.endTime)
        ),
    }));
}
